import { useRef } from "react";
import { driver } from "driver.js";
import { useTourContext } from "../contexts/TourContext";

// Estilos CSS para driver.js
import "driver.js/dist/driver.css";
import "../styles/tour.css";

const useTour = () => {
  const tourContext = useTourContext();
  const {
    isActive,
    currentPage,
    currentStep,
    continueTour,
    completedSteps,
    totalSteps,
    startTour,
    endTour,
    setCurrentStep,
    setCurrentPage,
    setContinueTour,
    completeStep,
    resetTour,
    setStep3Active,
  } = tourContext;

  const driverRef = useRef(null);
  const tourExecutingRef = useRef(false);

  // Función para limpiar la instancia actual
  const cleanupCurrentInstance = () => {
    tourExecutingRef.current = false;

    if (driverRef.current) {
      try {
        driverRef.current.destroy();
      } catch (error) {
        // Error silencioso al destruir driver instance
      }
      driverRef.current = null;
    }

    // Limpiar clases del DOM
    document.body.classList.remove("tour-active");
    document.body.style.removeProperty("pointer-events");
    document.body.style.removeProperty("overflow");

    // Limpiar elementos del DOM
    try {
      document
        .querySelectorAll(".driver-popover, .driver-overlay, .driver-backdrop, .driver-stage")
        .forEach((element) => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });

      document
        .querySelectorAll(".driver-highlighted, .driver-active-element, .driver-position-relative")
        .forEach((element) => {
          element.classList.remove("driver-highlighted", "driver-active-element", "driver-position-relative");
        });
    } catch (error) {
      // Error silencioso al limpiar elementos del DOM
    }
  };

  // Función para terminar el tour completamente
  const terminateTourCompletely = () => {
    cleanupCurrentInstance();
    endTour();
    setContinueTour(false);
    // Desactivar isStep3Active cuando el tour se cierra
    setStep3Active(false);
  };

  // Función para crear una nueva instancia de driver, acepta callback onTourEnd
  const ensureDriver = (config = {}, onTourEnd) => {
    cleanupCurrentInstance();

    driverRef.current = driver({
      showProgress: true,
      nextBtnText: "Siguiente",
      prevBtnText: "Anterior",
      doneBtnText: "¡Entendido!",
      progressText: "Paso {{current}} de {{total}}",
      showButtons: ["next", "previous", "close"],
      disableActiveInteraction: false, // CLAVE: Permitir interacción
      allowClose: true,
      overlayClickBehavior: "close",
      smoothScroll: true,
      animate: true,
      overlayOpacity: 0.6, // Reducir opacidad para mejor visibilidad
      stagePadding: 16, // Más padding para mejor acceso
      stageRadius: 8,
      // NUEVAS CONFIGURACIONES para mejorar interactividad
      allowKeyboardControl: true,
      disableButtons: false,
      allowClose: true,

      onCloseClick: () => {
        terminateTourCompletely();
        if (typeof onTourEnd === "function") onTourEnd();
      },

      onDoneClick: () => {
        terminateTourCompletely();
        if (typeof onTourEnd === "function") onTourEnd();
      },

      onCompleted: () => {
        terminateTourCompletely();
        if (typeof onTourEnd === "function") onTourEnd();
      },

      onClose: () => {
        cleanupCurrentInstance();
        if (typeof onTourEnd === "function") onTourEnd();
      },

      onDestroyed: () => {
        cleanupCurrentInstance();
        if (typeof onTourEnd === "function") onTourEnd();
      },

      ...config,
    });

    return driverRef.current;
  };

  // Función para detener el tour
  const stopTour = () => {
    terminateTourCompletely();
  };

  // Función para iniciar el tour de una página específica, acepta callback onTourEnd
  const startPageTour = async (pageName, steps, onTourEnd) => {
    if (tourExecutingRef.current) {
      return;
    }

    tourExecutingRef.current = true;

    try {
      // Activar tour si no está activo
      if (!isActive) {
        startTour(pageName);
      }

      // Establecer página actual
      setCurrentPage(pageName);

      // Esperar un momento para que se renderice la página
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Verificar que los elementos existen
      const validSteps = steps.filter((step) => {
        if (step.element) {
          const element = document.querySelector(step.element);
          return element !== null;
        }
        return true; // Para pasos sin elemento específico
      });

      if (validSteps.length === 0) {
        tourExecutingRef.current = false;
        return;
      }

      // Crear y ejecutar tour
      const driverInstance = ensureDriver(
        {
          steps: validSteps,
          onCompleted: () => {
            completeStep(pageName);
            terminateTourCompletely();
            if (typeof onTourEnd === "function") onTourEnd();
          },
        },
        onTourEnd
      );

      document.body.classList.add("tour-active");
      driverInstance.drive();
    } catch (error) {
      tourExecutingRef.current = false;
    } finally {
      tourExecutingRef.current = false;
    }
  };

  // Función para verificar si una página está completada
  const isStepCompleted = (stepKey) => {
    return completedSteps.includes(stepKey);
  };

  // Función para obtener el progreso del tour
  const getTourProgress = () => {
    // Mostrar al menos 1 completado para ver el progreso visual
    const adjustedCompleted = Math.max(1, completedSteps.length);

    return {
      completed: adjustedCompleted,
      total: totalSteps,
      percentage: Math.round((adjustedCompleted / totalSteps) * 100),
    };
  };

  return {
    // Estados
    isActive,
    currentPage,
    currentStep,
    continueTour,
    completedSteps,
    totalSteps,

    // Acciones
    startTour,
    endTour,
    stopTour,
    terminateTourCompletely,
    startPageTour,
    setCurrentStep,
    setCurrentPage,
    setContinueTour,
    completeStep,
    resetTour,
    cleanupCurrentInstance,

    // Utilidades
    isStepCompleted,
    getTourProgress,
  };
};

export default useTour;
